import React, { useState, useRef } from "react";
import { Radio, Video, MapPin, Clock, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getUpcomingOrActiveLive } from "@/services/eventsService";
import { URL_REGEX, stripTrailingPunct, openExternalUrl } from "@/utils/linkify";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";

function renderDescriptionLinks(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = new RegExp(URL_REGEX.source, "g");
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const raw = match[0];
    const url = stripTrailingPunct(raw);
    const trailing = raw.slice(url.length);
    parts.push(
      <a
        key={`url-${match.index}`}
        href={url}
        className="text-primary underline break-all"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          void openExternalUrl(url);
        }}
      >
        {url}
      </a>,
    );
    if (trailing) parts.push(trailing);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  if (parts.length === 0) parts.push(text);
  return parts;
}

// Mock data for preview mode (?preview-live or ?preview-live=countdown)
const PREVIEW_LIVE_EVENT = {
  id: 'preview',
  title: 'שיעור יוגה בוקר',
  description: 'שיעור יוגה לכל הרמות עם המדריכה שרון',
  start_time: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours from now
  end_time: new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString(),
  location: 'זום',
  host_name: 'שרון כהן',
  stream_url: '',
  cover_image: '',
  event_type: 'live' as const,
  is_live: false,
};

const PREVIEW_LIVE_NOW_EVENT = {
  ...PREVIEW_LIVE_EVENT,
  start_time: new Date(Date.now() - 10 * 60 * 1000).toISOString(), // started 10 min ago
  end_time: new Date(Date.now() + 50 * 60 * 1000).toISOString(),
  is_live: true,
};

export const LiveStreamBanner = React.memo(function LiveStreamBanner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const startXRef = useRef<number>(0);
  const isDraggingRef = useRef(false);

  // Preview mode: ?preview-live shows countdown, ?preview-live=now shows live-now
  const previewMode = searchParams.get('preview-live');
  const isPreview = previewMode !== null;

  // שליפת לייב (3 שעות לפני עד סוף האירוע)
  const { data: fetchedEvent } = useQuery({
    queryKey: ['live-banner-event'],
    queryFn: () => getUpcomingOrActiveLive(3), // 3 שעות לפני
    refetchInterval: 30000, // רענון כל 30 שניות
    enabled: !isPreview,
  });

  const liveEvent = isPreview
    ? (previewMode === 'now' ? PREVIEW_LIVE_NOW_EVENT : PREVIEW_LIVE_EVENT)
    : fetchedEvent;

  // בדיקה אם האירוע כבר התחיל (בשידור עכשיו)
  const isCurrentlyLive = () => {
    if (!liveEvent) return false;
    const now = new Date();
    const start = new Date(liveEvent.start_time);
    const end = new Date(liveEvent.end_time);
    return now >= start && now <= end;
  };

  // חישוב זמן הספירה לאחור
  const getTimeUntilLive = () => {
    if (!liveEvent) return 0;
    const start = new Date(liveEvent.start_time);
    const now = new Date();
    return Math.max(0, Math.floor((start.getTime() - now.getTime()) / 1000));
  };

  const secondsUntilLive = getTimeUntilLive();
  const isLiveNow = isCurrentlyLive();
  
  // פורמט הזמן
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const [currentTime, setCurrentTime] = React.useState(formatTime(secondsUntilLive));

  React.useEffect(() => {
    if (isLiveNow) return; // לא צריך ספירה לאחור אם יש לייב פעיל
    
    const interval = setInterval(() => {
      const newSeconds = getTimeUntilLive();
      setCurrentTime(formatTime(newSeconds));
    }, 1000);

    return () => clearInterval(interval);
  }, [liveEvent, isLiveNow]);

  const handleDragStart = (clientX: number) => {
    startXRef.current = clientX;
    isDraggingRef.current = true;
  };

  const handleDragMove = (clientX: number) => {
    if (!isDraggingRef.current) return;
    const diff = clientX - startXRef.current;
    setTranslateX(diff);
  };

  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    const threshold = 80; // minimum swipe distance to dismiss

    if (Math.abs(translateX) > threshold) {
      setIsDismissed(true);
    } else {
      setTranslateX(0);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => handleDragStart(e.touches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => handleDragMove(e.touches[0].clientX);
  const handleTouchEnd = () => handleDragEnd();
  const handleMouseDown = (e: React.MouseEvent) => handleDragStart(e.clientX);
  const handleMouseMove = (e: React.MouseEvent) => handleDragMove(e.clientX);
  const handleMouseUp = () => handleDragEnd();
  const handleMouseLeave = () => {
    if (isDraggingRef.current) handleDragEnd();
  };

  // Suppress click after a drag-dismiss gesture
  const dragDistance = Math.abs(translateX);
  const handleBannerClick = () => {
    if (dragDistance > 5) return;
    setIsDrawerOpen(true);
  };
  // The banner is a div (it has to take touch/mouse drag); give it what a
  // button would have, so VoiceOver and keyboards can open it too.
  const handleBannerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsDrawerOpen(true);
    }
  };
  // Swiping was the only way to hide the banner; this is the non-gesture path.
  const hideBanner = () => {
    setIsDrawerOpen(false);
    setIsDismissed(true);
  };

  // אם אין לייב או הבאנר נדחה - לא מציגים
  if (isDismissed || !liveEvent) return null;


  const handleJoinStream = () => {
    if (liveEvent.stream_url) {
      openExternalUrl(liveEvent.stream_url);
    }
  };

  // פורמט תאריך ושעה לתצוגה במגירה
  const formatEventDateTime = () => {
    const start = new Date(liveEvent.start_time);
    return format(start, "EEEE, d בMMMM בשעה HH:mm", { locale: he });
  };

  // אם הלייב כבר התחיל (בשידור עכשיו)
  if (isLiveNow) {
    return (
      <>
        <div className="-mt-4 mb-2 px-2 select-none">
          <div 
            className="bg-card border border-destructive/20 rounded-xl shadow-sm px-3 py-2 flex flex-row-reverse items-center justify-between cursor-pointer active:shadow-md"
            style={{ 
              transform: `translateX(${translateX}px)`,
              opacity: 1 - Math.abs(translateX) / 200,
              transition: isDraggingRef.current ? 'none' : 'transform 200ms, opacity 200ms, box-shadow 200ms',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onClick={handleBannerClick}
            onKeyDown={handleBannerKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`${liveEvent.title}: לייב בשידור עכשיו. פתיחת הפרטים`}
          >
            <Radio className="h-4 w-4 text-destructive" />
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-foreground">{liveEvent.title} לייב</span>
              <span className="text-base font-bold text-destructive">עכשיו</span>
            </div>
          </div>
        </div>

        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader className="text-right pe-4">
              <DrawerTitle className="text-xl text-right">{liveEvent.title}</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-[calc(env(safe-area-inset-bottom,0px)+48px)] space-y-4 overflow-y-auto">
              {liveEvent.cover_image && (
                <img 
                  src={liveEvent.cover_image} 
                  alt={liveEvent.title}
                  className="w-full h-48 object-cover rounded-xl"
                />
              )}
              
              <div className="flex items-center gap-2 text-destructive font-medium">
                <Radio className="h-4 w-4 animate-pulse" />
                <span>בשידור חי עכשיו</span>
              </div>

              {liveEvent.description && (
                <p className="text-muted-foreground text-sm whitespace-pre-wrap break-words">
                  {renderDescriptionLinks(liveEvent.description)}
                </p>
              )}

              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatEventDateTime()}</span>
                </div>
                {liveEvent.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>{liveEvent.location}</span>
                  </div>
                )}
                {liveEvent.host_name && (
                  <div className="flex items-center gap-2">
                    <span>מנחה: {liveEvent.host_name}</span>
                  </div>
                )}
              </div>

              <Button 
                className="w-full" 
                onClick={handleJoinStream}
                disabled={!liveEvent.stream_url}
              >
                <Video className="h-4 w-4 ms-2" />
                הצטרפות
              </Button>
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={hideBanner}>
                הסתרת ההודעה
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // לייב קרוב - ספירה לאחור
  return (
    <>
      <div className="-mt-4 mb-2 px-2 select-none">
        <div 
          className="bg-card border border-destructive/20 rounded-xl shadow-sm px-3 py-2 flex flex-row-reverse items-center justify-between cursor-pointer active:shadow-md"
          style={{ 
            transform: `translateX(${translateX}px)`,
            opacity: 1 - Math.abs(translateX) / 200,
            transition: isDraggingRef.current ? 'none' : 'transform 200ms, opacity 200ms, box-shadow 200ms',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onClick={handleBannerClick}
          onKeyDown={handleBannerKeyDown}
          role="button"
          tabIndex={0}
          aria-label={`${liveEvent.title}: לייב בקרוב. פתיחת הפרטים`}
        >
          <Radio className="h-4 w-4 text-destructive" />
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-foreground">{liveEvent.title} לייב</span>
            <span className="text-base text-destructive font-mono font-bold">{currentTime}</span>
          </div>
        </div>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-right pe-4">
            <DrawerTitle className="text-xl text-right">{liveEvent.title}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-[calc(env(safe-area-inset-bottom,0px)+48px)] space-y-4 overflow-y-auto">
            {liveEvent.cover_image && (
              <img 
                src={liveEvent.cover_image} 
                alt={liveEvent.title}
                className="w-full h-48 object-cover rounded-xl"
              />
            )}
            
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>מתחיל בעוד {currentTime}</span>
            </div>

            {liveEvent.description && (
              <p className="text-muted-foreground text-sm">{liveEvent.description}</p>
            )}

            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatEventDateTime()}</span>
              </div>
              {liveEvent.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>{liveEvent.location}</span>
                </div>
              )}
              {liveEvent.host_name && (
                <div className="flex items-center gap-2">
                  <span>מנחה: {liveEvent.host_name}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {liveEvent.stream_url && (
                <Button
                  className="w-full"
                  onClick={handleJoinStream}
                >
                  <Video className="h-4 w-4 ms-2" />
                  פתיחת הזום
                </Button>
              )}
              <Button
                className="w-full"
                variant={liveEvent.stream_url ? "outline" : "default"}
                onClick={() => {
                  setIsDrawerOpen(false);
                  navigate(`/events/${liveEvent.id}`);
                }}
              >
                <Video className="h-4 w-4 ms-2" />
                {liveEvent.stream_url ? "פרטי האירוע" : "הרשמה לשידור"}
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  setIsDrawerOpen(false);
                  navigate('/events');
                }}
              >
                <Calendar className="h-4 w-4 ms-2" />
                צפייה בלוח האירועים
              </Button>
              <Button variant="ghost" className="w-full text-muted-foreground" onClick={hideBanner}>
                הסתרת ההודעה
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
});
